import {
	Center,
	type CenterProps,
	Flex,
	type FlexProps,
	ScrollArea,
	Space,
	Table,
	type TableProps,
	Text,
} from '@mantine/core';
import { useState } from 'react';
import type { UsePaginationReturn } from '../hooks/usePagination';
import { useResponsiveSpace } from '../hooks/useResponsiveSpace';
import { LoadingRows } from './LoadingRows';
import { PageSizeSelector } from './PageSizeSelector';
import { Paper } from './Paper';
import { TablePageSelector } from './TablePageSelector';
import { KeyedRow } from './KeyedRow';

interface StickyTableProps extends TableProps {
	pagination?: UsePaginationReturn;
	totalItems?: number;
	rows?: number;
	columnSpan?: number;
	hidePageSizeSelector?: boolean;
	highlightOnHover?: boolean;
	isRowsLoading?: boolean;
	isPaginationLoading?: boolean;
	minHeight?: string;
	maxHeight?: string;
	width?: string;
	hidePagination?: boolean;
	withBorder?: boolean;
	withStripes?: boolean;
}

export function DataTable({
	pagination,
	totalItems,
	rows,
	minHeight = '74vh',
	maxHeight = '81vh',
	width = '100%',
	highlightOnHover = false,
	isRowsLoading = false,
	isPaginationLoading = false,
	withBorder = false,
	withStripes = true,
	hidePagination = false,
	hidePageSizeSelector = false,
	stickyHeader = true,
	...tableProps
}: StickyTableProps) {
	const [, setScrolled] = useState(false);

	const noValuesFound = totalItems === 0;

	const notEnoughValues = totalItems && totalItems <= 10;
	const responsiveHeight = useResponsiveSpace([
		[753, minHeight],
		[1048, maxHeight],
	]);
	// const responsiveHeight = window.

	return (
		<Paper withPadding>
			<Flex
				direction={'column'}
				h={responsiveHeight}
				w={width}
				justify={'space-between'}
			>
				<ScrollArea
					w={width ?? '100%'}
					onScrollPositionChange={({ y }) => setScrolled(y !== 0)}
				>
					<Table
						withRowBorders={!isRowsLoading}
						withTableBorder={withBorder}
						highlightOnHover={highlightOnHover}
						stickyHeader={stickyHeader}
						{...tableProps}
					>
						{tableProps.children}
						{isRowsLoading && (
							<LoadingRows columnSpan={200} rows={rows ?? 10} />
						)}
					</Table>
					{noValuesFound && <NoDataFoundMessage />}
				</ScrollArea>
				{!hidePagination &&
					pagination?.value &&
					!noValuesFound &&
					!notEnoughValues && (
						<>
							<Space h={18} />
							<Flex
								w={'100%'}
								justify={hidePageSizeSelector ? 'center' : 'flex-end'}
							>
								<Flex justify={'flex-end'}>
									{hidePageSizeSelector ? (
										<></>
									) : (
										<>
											<PageSizeSelector
												pageSize={pagination.value.pageSize}
												setPageSize={pagination.setPageSize}
											/>
											<Space w={10} />
										</>
									)}
									<TablePageSelector
										pageNumber={pagination.value.pageNumber}
										setPageNumber={pagination.setPageNumber}
										totalPages={pagination.getPageCount(totalItems) ?? 300}
										isLoading={isPaginationLoading}
									/>
								</Flex>
							</Flex>
						</>
					)}
			</Flex>
		</Paper>
	);
}

interface NoDataFoundMessageProps extends FlexProps {
	paddingTop?: number;
	size?: string;
}

export function NoDataFoundMessage({
	paddingTop = 40,
	size = 'sm',
	...flexProps
}: NoDataFoundMessageProps) {
	return (
		<Center w={'70dw'}>
			<Flex align={'center'} pt={paddingTop} {...flexProps}>
				<Text size={size}>
					<i>No data to display {':('}</i>
				</Text>
			</Flex>
		</Center>
	);
}

export function NoDataFoundTableRow({ ...centerProps }: CenterProps) {
	return (
		<KeyedRow>
			<Table.Td colSpan={100}>
				<Center {...centerProps}>
					<i>No data to display {':('}</i>
				</Center>
			</Table.Td>
		</KeyedRow>
	);
}
